import type { ButtonProps } from '@hover/blueprint';
import { Box, Button, Heading, Panel } from '@hover/blueprint';
import { motion } from '@hover/blueprint/foundation';
import { transitions } from 'polished';
import React, { useContext, useState } from 'react';

import { ColorLabel } from 'src/modules/designStudio/components/DesignMenu/ColorPicker/ColorLabel';
import { ExpandButton } from 'src/modules/designStudio/components/DesignMenu/ColorPicker/ExpandButton';
import { ExpandHeader } from 'src/modules/designStudio/components/DesignMenu/ColorPicker/ExpandHeader';
import { PillList } from 'src/modules/designStudio/components/DesignMenu/PillList';
import { UIKind } from 'src/modules/designStudio/components/DesignStudio/UILayer/types';
import { usePartner } from 'src/modules/designStudio/hooks';
import { Partner, uiLayerContext } from 'src/modules/designStudio/providers';
import type { DesignStudioColor } from 'src/shared/graphql/generated';
import { Page, buttonPressed } from 'src/shared/telemetry';

interface ColorPalletProps {
  items: DesignStudioColor[];
  selectedColor?: string;
  selectedGroup: string;
  onClick: (color: DesignStudioColor) => void;
}

interface ColorPickerProps {
  items: DesignStudioColor[];
  header: React.ReactNode | string;
  groups: string[];
  groupLabelColors: { [key: string]: string };
  selectedColor?: string;
  selectedColorName?: string;
  selectedGroup: string;
  setColorGroup: (group: string) => void;
  changeColor: (color: DesignStudioColor) => void;
  subHeader?: React.ReactNode;
}

interface ColorButtonProps extends ButtonProps {
  backgroundColor: string;
  isSelected: boolean;
}

const ColorButton = ({
  isSelected,
  backgroundColor,
  ...props
}: ColorButtonProps) => (
  <Button
    {...props}
    _after={{
      position: 'absolute',
      opacity: isSelected ? 1 : 0,
      content: '""',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '15px',
      height: '15px',
      borderRadius: 900,
      backgroundColor: 'neutral.0',
      boxShadow:
        '0px 0px 3px rgba(0, 0, 0, 0.18), 0px 2px 4px rgba(0, 0, 0, 0.12)',
      ...transitions(['opacity'], motion.easings[250]),
    }}
    _hover={{
      borderColor: 'primary.300',
    }}
    borderColor={isSelected ? 'primary.base' : backgroundColor}
    borderWidth="3px"
    sx={{
      backgroundColor,
      '&:hover:not([disabled]), &:active:not([disabled])': {
        backgroundColor,
      },
    }}
  />
);

const ColorPallet = ({
  items,
  selectedColor,
  onClick,
  selectedGroup,
}: ColorPalletProps) => (
  <Panel
    borderColor="neutral200"
    borderStyle="solid"
    borderWidth="2px"
    display="grid"
    gridArea={0}
    gridColumnGap={50}
    gridRowGap={50}
    gridTemplateColumns="auto auto auto auto auto"
    margin={0}
    padding={50}
  >
    {items.map(item => (
      <ColorButton
        backgroundColor={item.color}
        borderRadius="0"
        fill="minimal"
        height="50px"
        isSelected={item.color === selectedColor}
        key={`${selectedGroup}-${item.color}`}
        label={`${item.name}-${item.color}`}
        onClick={() => onClick(item)}
      />
    ))}
  </Panel>
);

const ColorPicker = ({
  header,
  items,
  groups,
  groupLabelColors,
  selectedColor,
  selectedColorName,
  selectedGroup,
  setColorGroup,
  changeColor,
  subHeader,
}: ColorPickerProps) => {
  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const [isPopoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [lastPopoverOnClose, setLastPopoverOnClose] = useState<number>(
    Date.now(),
  );
  const { kind } = useContext(uiLayerContext);

  const partner = usePartner();

  const showColorLabel = partner === Partner.JAMES_HARDIE;

  const debouncedPopoverToggle = () => {
    if (Date.now() - lastPopoverOnClose > 150) {
      setPopoverOpen(!isPopoverOpen);
    }
  };

  const selectColorGroup = (colorGroup: string) => {
    const buttonPressedTrackingData = {
      button_text: colorGroup,
      item_type: 'colorGroup',
      page_or_screen_name: Page.DESIGN_STUDIO,
      primary_cta: false,
    };

    setColorGroup(colorGroup);
    buttonPressed(buttonPressedTrackingData);
  };

  const expandHeader = (
    <ExpandHeader
      groupLabelColors={groupLabelColors}
      groups={groups}
      isOpen={isPopoverOpen}
      onClose={() =>
        setPopoverOpen(() => {
          setLastPopoverOnClose(Date.now());
          return false;
        })
      }
      onOpen={() => setPopoverOpen(true)}
      selectedGroup={selectedGroup}
      setColorGroup={setColorGroup}
    />
  );

  const expandButton =
    kind === UIKind.LANDSCAPE ? (
      <ExpandButton onClick={debouncedPopoverToggle} />
    ) : undefined;

  return (
    <Box
      display="block"
      flexDirection="column"
      marginBottom={600}
      onBlur={() => setHasFocus(false)}
      onFocus={() => setHasFocus(true)}
    >
      {groups.length > 1 ? (
        <>
          <PillList
            enableScrollIntoView={hasFocus}
            expandButton={expandButton}
            expandHeader={expandHeader}
            header={header}
            items={groups}
            marginBottom={0}
            onClick={selectColorGroup}
            selected={selectedGroup}
            subHeader={subHeader}
            wrapItems={false}
          />
          {showColorLabel && (
            <ColorLabel
              selectedColor={selectedColor}
              selectedColorName={selectedColorName}
            />
          )}
          <ColorPallet
            items={items}
            onClick={changeColor}
            selectedColor={selectedColor}
            selectedGroup={selectedGroup}
          />
        </>
      ) : (
        <>
          <Heading marginBottom={300} size={200}>
            {header}
          </Heading>
          {subHeader}
          {showColorLabel && (
            <ColorLabel
              selectedColor={selectedColor}
              selectedColorName={selectedColorName}
            />
          )}

          <ColorPallet
            items={items}
            onClick={changeColor}
            selectedColor={selectedColor}
            selectedGroup={selectedGroup}
          />
        </>
      )}
    </Box>
  );
};

export { ColorPicker };
